// La page d'un groupe : son en-tête, ses membres, et son fil propre.
//
// Un groupe n'est pas une page : on y échange. Tout membre y publie, et ce qui
// s'y dit ne sort pas dans le fil public — c'est ce qui permet à un groupe de
// prière d'exister sans être une tribune.
//
// Une subtilité de l'ancienne version qui disparaît ici : elle prenait soin de
// ne reconstruire les actions et le composeur QUE si l'appartenance avait
// changé, sinon un simple rafraîchissement de la liste des membres effaçait le
// texte en cours de frappe. C'était du soin manuel, à ne pas oublier. En React,
// le texte est un état du composeur : la liste des membres peut changer tant
// qu'elle veut, elle ne le touche pas.

import { useEffect, useState } from "react";
import Store from "../../store.js";
import { toast, confirmer } from "../../ui.js";
import { typeDe } from "../../espaces-meta.js";
import Avatar from "../Avatar.jsx";
import Certifie from "../Certifie.jsx";
import Icone from "../Icone.jsx";
import TexteRiche from "../TexteRiche.jsx";
import ComposeurEspace from "../ComposeurEspace.jsx";
import ListePublications from "../ListePublications.jsx";
import Vide, { Chargement } from "../Vide.jsx";
import { useAbonnement } from "../hooks.js";

function Membres({ membres }) {
  if (!membres.length) return <Vide symbole="👥" titre="Aucun membre" />;
  return (
    <div className="grille-membres">
      {membres.map((u) => (
        <a className="membre" href={`#/profil/${u.uid}`} key={u.uid}>
          <span className="membre__qui">
            <Avatar membre={u} taille={44} />
            <span>
              <strong>
                {u.nom}
                <Certifie membre={u} taille={13} />
              </strong>
              <span className="petit texte-doux">
                {u.role === "responsable" ? "Responsable" : u.eglise || u.ville || "Membre"}
              </span>
            </span>
          </span>
        </a>
      ))}
    </div>
  );
}

export default function Groupe({ ctx, params }) {
  const id = params.id;
  const [etat, setEtat] = useState({ phase: "chargement" });
  const membres = useAbonnement((cb) => Store.groupes.subscribeMembres(id, cb), [], [id]);

  useEffect(() => {
    let vivant = true;
    setEtat({ phase: "chargement" });
    Store.groupes
      .get(id)
      .then((g) => vivant && setEtat(g ? { phase: "prete", g } : { phase: "introuvable" }))
      .catch(() => vivant && setEtat({ phase: "introuvable" }));
    return () => {
      vivant = false;
    };
  }, [id]);

  if (etat.phase === "chargement")
    return (
      <div className="colonne">
        <Chargement texte="Chargement du groupe…" />
      </div>
    );

  if (etat.phase === "introuvable")
    return (
      <div className="colonne">
        <Vide symbole="👥" titre="Groupe introuvable" texte="Il a peut-être été supprimé." />
      </div>
    );

  const g = etat.g;
  const t = typeDe(g.type);
  const moi = membres.find((m) => m.uid === ctx.moi.uid);
  const jeSuisMembre = Boolean(moi);
  const responsable = moi?.role === "responsable";

  const rejoindre = async () => {
    try {
      await Store.groupes.rejoindre(id);
      toast(`Bienvenue dans ${g.nom} 🙌`);
    } catch (e) {
      toast(e.message || "Impossible de rejoindre", "erreur");
    }
  };

  const quitter = async () => {
    if (await confirmer("Quitter ce groupe ?", "Vous ne verrez plus ses publications.", "Quitter"))
      await Store.groupes.quitter(id);
  };

  const supprimer = async () => {
    if (
      await confirmer("Supprimer ce groupe ?", "Le groupe et toutes ses publications seront perdus.", "Supprimer")
    ) {
      await Store.groupes.supprimer(id);
      toast("Groupe supprimé");
      location.hash = "#/groupes";
    }
  };

  return (
    <div className="colonne">
      <section className="groupe-tete">
        <div className="groupe-tete__banniere">{t.icone}</div>
        <div className="groupe-tete__corps">
          <h1>{g.nom}</h1>
          <p className="petit texte-doux">
            {t.label} · {membres.length} membre{membres.length > 1 ? "s" : ""}
          </p>
          {g.description && (
            <p className="groupe-tete__desc">
              <TexteRiche texte={g.description} />
            </p>
          )}
          <div className="profil__actions">
            {jeSuisMembre ? (
              <>
                <button className="btn btn--gris" onClick={quitter}>
                  Quitter le groupe
                </button>
                {responsable && (
                  <button
                    className="btn btn--gris btn--carre"
                    aria-label="Supprimer le groupe"
                    onClick={supprimer}
                  >
                    <Icone nom="fermer" taille={18} />
                  </button>
                )}
              </>
            ) : (
              <button className="btn btn--primaire" onClick={rejoindre}>
                <Icone nom="plus" taille={17} /> Rejoindre le groupe
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Un non-membre lit, mais ne publie pas. */}
      {jeSuisMembre && (
        <ComposeurEspace
          ctx={ctx}
          invite={`Écrire dans ${g.nom}…`}
          succes="Publié dans le groupe 🙌"
          champsEspace={{ groupeId: id }}
        />
      )}

      <h2 className="titre-section">Membres</h2>
      <Membres membres={membres} />

      <h2 className="titre-section">Publications du groupe</h2>
      <ListePublications
        ctx={ctx}
        chargerPage={(o) => Store.posts.page({ ...o, groupeId: id })}
        abonnerRecentes={(cb) => Store.posts.subscribeGroupe(id, cb)}
        vide={["🕊️", "Rien dans ce groupe", "Soyez le premier à y écrire."]}
        deps={[id]}
      />
    </div>
  );
}
